import { SocketClient } from '@goldcast/client';
import { throttle } from 'lodash';

import loggerService from '../logger.service';
import trackerService from '../tracker.service';
import { shouldRefreshAuthToken, getAuthTokenStatus } from './authUtils';
import {
  MISSED_PING_THRESHOLD_TIME,
  THROTTLE_TIMEOUT,
  ONE_MINUTE,
  PING_RESPONSE_TIMEOUT,
  TOKEN_EXPIRY_CHECK_INTERVAL,
  EXPIRY_RECONNECT_COOLDOWN
} from './constants';

import analytics from '@/analytics';
import { ChatEvents } from '@/libs/eventBus/chatEvents';
import EventBus from '@/libs/eventBus/eventBus';
import { mattermostApiClient } from '@/services/apiClient';
import { isTokenExpired } from '@/services/gcauth.service';
import { assignMattermostUserToTeam } from '@/services/mattermostUserAssignment.service';
import { recordMMSocketMessage } from '@/services/socketClient/socketStateTracker';
import store from '@/store';
import { SERVER_CLIENT_COMMUNICATION_MODE } from '@/store/modules/mattermost';
import {
  sendRoomUpdateEvent,
  processUpdateConnectionStatus,
  processSocketOpenDone
} from '@/store/modules/socketMessageProcessor';

let lastMMmsgTimeStamp = 0;
let pingTimeout: NodeJS.Timeout | null = null;
let pingResponseTimeout: NodeJS.Timeout | null = null;
let awaitingPingResponse = false;
let tokenExpiryCheckTimer: ReturnType<typeof setInterval> | null = null;
let lastExpiryReconnectAt = 0;

export class MMClient extends SocketClient {
  // this starts from 2 because we have seq 1 for auth challenge, so we need to start counter from 2
  public counter = 2;
  get bailedStateThresold() {
    if (store.getters['eventsource/shouldUseSSE']) {
      return 6;
    }
    return 20;
  }
  constructor(url: string, logger: typeof loggerService) {
    super(url, logger, 'mmsocket');
  }

  async preConnect(): Promise<void> {
    this.logger.info('Running socket preConnect for mmsocket');
    trackerService.track('mattermost', 'shouldBeUsed', { isEnabled: true });
    const currentUser = store.state.user.currentUser;
    const currentEvent = store.state.event.event;
    if (!currentUser?.mmid && currentEvent?.mmid && currentUser?.id) {
      const assignmentResult = await assignMattermostUserToTeam({
        userId: currentUser.id,
        teamId: currentEvent.mmid,
        eventId: currentEvent.id
      });
      if (assignmentResult.status === 'blocked') {
        loggerService.error('[mmsocket] Blocked: Mattermost user assignment during preConnect');
      } else if (assignmentResult.status === 'failed') {
        loggerService.error('[mmsocket] Failed to create Mattermost user assignment', assignmentResult.error);
      }
    }

    const authStatus = getAuthTokenStatus();
    loggerService.info('[mmsocket] preConnect - checking auth', {
      hasToken: authStatus.hasToken,
      isJwt: authStatus.isJwt,
      tokenExpired: authStatus.tokenExpired
    });

    if (shouldRefreshAuthToken()) {
      loggerService.info('[mmsocket] preConnect - token needs refresh');
      return mattermostApiClient.onUnauthorized() as Promise<void>;
    }
    loggerService.info('[mmsocket] preConnect - token is valid, proceeding with connection');
  }

  onError(reconnectionAttempt: number) {
    if (reconnectionAttempt > 5) {
      trackerService.track('mattermost', 'retry');
      analytics.track('mm-socket-errored-out', {
        reconnectionAttempt,
        socket: 'mattermost',
        event_id: store.state.event.event?.id,
        role: store.state.user.currentEventUser?.event_role
      });
    }
    loggerService.error('[mmsocket] Connection error', { reconnectionAttempt });
    if (reconnectionAttempt === 1) {
      trackerService.track('mattermost', 'connectionInitialized', { timestamp: Date.now() });
    }
    trackerService.track('mattermost', 'error');
    this.clearMissedPingTimer();
    this.clearTokenExpiryCheck();
    store.commit('mattermost/setSocketStatus', 'error');
    return processUpdateConnectionStatus({
      status: 'error',
      firstConnectDone: store.state.mattermost.firstConnectDone
    });
  }
  updateMissedPingTimer = throttle(() => {
    this.setMissedPingTimer();
  }, THROTTLE_TIMEOUT);

  setMissedPingTimer() {
    this.clearMissedPingTimer();
    pingTimeout = setTimeout(() => {
      this.checkForInActivityAndReconnectIfNeeded();
    }, MISSED_PING_THRESHOLD_TIME);
  }

  clearMissedPingTimer() {
    if (pingTimeout) {
      clearTimeout(pingTimeout);
    }
    if (pingResponseTimeout) {
      clearTimeout(pingResponseTimeout);
      pingResponseTimeout = null;
    }
    awaitingPingResponse = false;
  }

  // Periodically decodes the access token locally (no network) and, the moment
  // it has expired, reconnects.
  startTokenExpiryCheck(): void {
    this.clearTokenExpiryCheck();
    if (!mattermostApiClient.isJwtAuth) {
      return;
    }
    loggerService.info('[mmsocket] Starting token expiry check (every 10s)');
    tokenExpiryCheckTimer = setInterval(() => {
      const authToken = mattermostApiClient.authToken;
      loggerService.log('[mmsocket] Checking token expiry', {
        hasToken: !!authToken,
        isExpired: authToken ? isTokenExpired(authToken) : undefined
      });

      if (!authToken || !isTokenExpired(authToken)) {
        return;
      }

      const timeSinceLastReconnect = Date.now() - lastExpiryReconnectAt;
      if (timeSinceLastReconnect < EXPIRY_RECONNECT_COOLDOWN) {
        loggerService.info('[mmsocket] Token expired but cooldown active, skipping reconnect', {
          timeSinceLastReconnect
        });
        return;
      }
      lastExpiryReconnectAt = Date.now();
      loggerService.error('[mmsocket] Access token expired, reconnecting to refresh');
      store
        .dispatch('mattermost/disconnect')
        .then(() => store.dispatch('mattermost/connect'))
        .catch(err => loggerService.error('[mmsocket] Failed to reconnect on token expiry', err));
    }, TOKEN_EXPIRY_CHECK_INTERVAL);
  }

  clearTokenExpiryCheck(): void {
    if (tokenExpiryCheckTimer) {
      clearInterval(tokenExpiryCheckTimer);
      tokenExpiryCheckTimer = null;
    }
  }

  disconnect(): void {
    this.clearMissedPingTimer();
    this.clearTokenExpiryCheck();
    super.disconnect();
  }

  sendPingAndWaitForResponse() {
    loggerService.info('[mmsocket] Sending ping to check connection health');
    awaitingPingResponse = true;
    this.sendMessage({
      seq: this.counter++,
      action: 'ping',
      data: {}
    });

    pingResponseTimeout = setTimeout(() => {
      if (awaitingPingResponse) {
        loggerService.error('[mmsocket] No OK response to ping within 10s, reconnecting');
        awaitingPingResponse = false;
        this.clearMissedPingTimer();
        store
          .dispatch('mattermost/disconnect')
          .then(() => store.dispatch('mattermost/connect'))
          .catch(err => loggerService.error('[mmsocket] Failed to reconnect after ping timeout', err));
      }
    }, PING_RESPONSE_TIMEOUT);
  }

  handlePingResponse() {
    loggerService.info('[mmsocket] Received OK response to ping, connection is healthy');
    awaitingPingResponse = false;
    if (pingResponseTimeout) {
      clearTimeout(pingResponseTimeout);
      pingResponseTimeout = null;
    }
    lastMMmsgTimeStamp = Date.now();
    this.setMissedPingTimer();
  }

  handlePingFailure(_error: unknown) {
    loggerService.error('[mmsocket] Received FAIL response to ping, reconnecting');
    awaitingPingResponse = false;
    this.clearMissedPingTimer();
    store
      .dispatch('mattermost/disconnect')
      .then(() => store.dispatch('mattermost/connect'))
      .catch(err => loggerService.error('[mmsocket] Failed to reconnect after ping failure', err));
  }

  checkForInActivityAndReconnectIfNeeded() {
    const isLastMMmsgOlderThanOneMinute = Date.now() - lastMMmsgTimeStamp > ONE_MINUTE;
    if (isLastMMmsgOlderThanOneMinute) {
      loggerService.info('[mmsocket] No messages in last minute, sending ping to verify connection');
      this.sendPingAndWaitForResponse();
      return;
    }
    this.updateMissedPingTimer();
  }

  onMessage(event: any) {
    try {
      const msg = JSON.parse(event.data);
      if (msg.status === 'OK' && awaitingPingResponse) {
        this.handlePingResponse();
        return;
      }
      if (msg.status === 'FAIL' && awaitingPingResponse) {
        this.handlePingFailure(msg.error);
        return;
      }
      if (msg.broadcast?.specific_team_id) {
        recordMMSocketMessage(msg.broadcast.specific_team_id);
      }
    } catch {
      loggerService.error('[mmsocket] Error parsing message from server', event.data);
    }

    lastMMmsgTimeStamp = Date.now();
    this.updateMissedPingTimer();
    return store.dispatch('mattermost/processEvent', { msg: event?.data, source: 'mmsocket' });
  }

  onClosed() {
    trackerService.track('mattermost', 'disconnections');
    this.clearMissedPingTimer();
    this.clearTokenExpiryCheck();
    store.commit('mattermost/setSocketStatus', 'offline');
    return processUpdateConnectionStatus({
      status: 'offline',
      firstConnectDone: store.state.mattermost.firstConnectDone
    });
  }

  onOpened() {
    EventBus.dispatch(ChatEvents.SocketAvailable, {
      socketInstance: this
    });
    loggerService.log('[chat-app] Socket connection opened');

    this.sendMessage({
      seq: 1,
      action: 'authentication_challenge',
      data: {
        token: mattermostApiClient.authToken
      }
    });
    store.commit('mattermost/updateMmEverConnected', true);
    // if socket is errored out and connected again then we need to send update room event as its a new socket
    sendRoomUpdateEvent();
    // this will ensure we atleast start a timer to detect missed pings from server after one minute.
    // this will keep getting updated as we get pings(messages) from server
    this.setMissedPingTimer();
    // start the local token-expiry watchdog so we proactively reconnect (with a
    // refreshed token) the moment the access token lapses.
    this.startTokenExpiryCheck();
    loggerService.info('[mmsocket] Connection fully established and ready');
    trackerService.track('mattermost', 'connectionEstablished', { timestamp: Date.now() });
    store.commit('mattermost/setSocketStatus', 'online');
    processSocketOpenDone();
    return processUpdateConnectionStatus({
      status: 'online',
      firstConnectDone: store.state.mattermost.firstConnectDone
    });
  }

  onBail() {
    trackerService.track('mattermost', 'disconnections');
    loggerService.warn('[mmsocket] Bailing on mattermost connection');
    this.clearTokenExpiryCheck();
    store.commit('event/avroom/setAllChannelsToLeft');
    store.commit('mattermost/setSocketStatus', 'bailed-noretry');
    store.commit('mediaroom/exitAllRoomsAsync');
    processUpdateConnectionStatus({
      status: 'bailed-noretry',
      firstConnectDone: store.state.mattermost.firstConnectDone
    });
    if (store.getters['eventsource/shouldUseSSE']) {
      store.dispatch('mattermost/disconnect').then(async () => {
        await store.dispatch('eventsource/connect', null, { root: true });
        store.commit('mattermost/setServerClientCommunicationMode', SERVER_CLIENT_COMMUNICATION_MODE.SSE);
      });
    }
  }
}
